import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

import { Card, CardContent } from '../ui/card';

import { adminRoutes, getAccessibleCategoryIds } from './navigation/adminRoutes';
import { getRememberedCategoryPath } from './navigation/adminPlaceMemory';
import { canAccessSection as checkSectionAccess, getAdminUser } from './navigation/sectionAccess';

const AdminDashboardHome = () => {
  // Get current user's permissions from localStorage (role + is_protected + allowed_sections)
  const adminUser = getAdminUser();

  // Helper: check if user can access a section (role-aware — see sectionAccess.js)
  const canAccessSection = (sectionId) => checkSectionAccess(sectionId, adminUser);

  // Get categories that have at least one accessible section
  const visibleCategories = getAccessibleCategoryIds(canAccessSection);

  // No access state
  if (visibleCategories.length === 0) {
    return (
      <Card className="max-w-md mx-auto admin-card">
        <CardContent className="p-8 text-center">
          <div className="p-4 rounded-full bg-stone-100 dark:bg-stone-800 w-fit mx-auto mb-4">
            <Shield className="h-12 w-12 text-stone-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">No Access Granted</h2>
          <p className="text-stone-600 dark:text-stone-400">
            Your account does not have access to any admin sections.
            Please contact a protected administrator to request access.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="text-center">
        <span className="text-4xl text-amber-600/20 font-serif select-none cursor-help hidden md:block" title="Dojo - Training Hall">
          道場
        </span>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">Welcome to Admin Panel</h1>
        <p className="text-stone-600 dark:text-stone-400 mt-1">Select a cockpit to get started</p>
      </div>

      {/* Quick Access Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {visibleCategories.map((categoryId) => {
          const category = adminRoutes[categoryId];
          const Icon = category.icon;
          return (
            <Link
              key={categoryId}
              to={getRememberedCategoryPath(categoryId, adminUser, canAccessSection)}
              className="group rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              <Card className="admin-card admin-corner-accent h-full border border-stone-200 transition-all duration-300 group-hover:border-amber-500 group-hover:shadow-xl">
                <CardContent className="flex flex-col items-center justify-center p-4 md:p-6 text-center min-h-[120px] md:min-h-[140px]">
                  <div className="p-2 rounded-full bg-gradient-to-br from-red-800 to-red-900 mb-2 group-hover:from-amber-600 group-hover:to-amber-700 transition-all duration-300">
                    <Icon className="h-6 w-6 md:h-8 md:w-8 text-amber-100" />
                  </div>
                  <span className="font-medium text-sm md:text-base text-gray-800 dark:text-gray-100">
                    {category.label}
                  </span>
                  <span className="text-xs text-stone-500 dark:text-stone-400 mt-1 hidden md:block">
                    {category.sections.filter((s) => canAccessSection(s.id)).length} sections
                  </span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Decorative Divider */}
      <div className="admin-divider">
        <span className="text-xl">⚔</span>
      </div>

      {/* Quick tips */}
      <div className="admin-card rounded-lg shadow-sm p-4 border border-stone-200 dark:border-stone-700">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
          <span className="text-amber-600">●</span>
          Quick Tips
        </h3>
        <ul className="text-sm text-stone-600 dark:text-stone-400 space-y-1">
          <li>• Use the sidebar to move between cockpit dashboards</li>
          <li>• On mobile, use More to open the complete cockpit list</li>
          <li>• Changes auto-save after 5 seconds of inactivity</li>
          <li>• Use the "View Site" button to preview your changes</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboardHome;
